import React, { useState } from "react";
import { Box, Button, CircularProgress, Typography } from "@mui/material";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import ModalShell from "../CollectionsTab/ModalShell";
import {
  AdminSurveyRequestSummary,
  SurveyRequestFile,
  downloadSurveyRequestFile,
} from "../../../api/derin";
import {
  collectionColors,
  font,
  formatFileSize,
  outlinedButtonSx,
  subtitleSx,
} from "../CollectionsTab/tokens";
import { useToast } from "../Toast";
import { blobErrorMessage, saveBlob } from "./format";

interface SurveyRequestFilesDialogProps {
  open: boolean;
  organizationId: string;
  request: AdminSurveyRequestSummary | null;
  onClose: () => void;
}

/**
 * The files a client attached to a request. Not in the Figma: it follows the dialogs of
 * the Collections tab until design settles on a layout.
 */
const SurveyRequestFilesDialog: React.FC<SurveyRequestFilesDialogProps> = ({
  open,
  organizationId,
  request,
  onClose,
}) => {
  const { showError } = useToast();
  const [downloadingId, setDownloadingId] = useState<string | null>(null);

  const handleDownload = async (file: SurveyRequestFile) => {
    if (!request) return;
    setDownloadingId(file.id);
    try {
      const blob = await downloadSurveyRequestFile(organizationId, request.id, file.id);
      // The name comes from the listing, not Content-Disposition: the browser does not
      // expose that header to the page unless the API lists it in CORS.
      saveBlob(blob, file.fileName);
    } catch (err) {
      console.error("Error downloading survey request file:", err);
      showError(`Error downloading file: ${await blobErrorMessage(err)}`);
    } finally {
      setDownloadingId(null);
    }
  };

  const files = request?.files ?? [];

  return (
    <ModalShell
      open={open}
      onClose={onClose}
      title="Attached files"
      subtitle={request ? `Files submitted with "${request.title}".` : undefined}
      width={560}
      confirmLabel="Close"
      onConfirm={onClose}
      hideCancel
    >
      {files.length === 0 ? (
        <Typography sx={subtitleSx}>No files attached to this request.</Typography>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: "8px" }}>
          {files.map((file) => (
            <Box
              key={file.id}
              data-testid={`survey-request-file-${file.id}`}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: "12px",
                padding: "12px",
                border: `1px solid ${collectionColors.border}`,
                borderRadius: "10px",
              }}
            >
              <InsertDriveFileOutlinedIcon
                sx={{ width: 20, height: 20, color: collectionColors.textSecondary, flexShrink: 0 }}
              />
              <Box sx={{ display: "flex", flexDirection: "column", minWidth: 0, flex: 1 }}>
                <Typography
                  sx={{
                    fontFamily: font,
                    fontWeight: 500,
                    fontSize: "14px",
                    lineHeight: "20px",
                    color: collectionColors.textPrimary,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {file.fileName}
                </Typography>
                <Typography sx={{ ...subtitleSx, fontSize: "12px", lineHeight: "16px" }}>
                  {file.kindLabel?.trim() || file.kind} · {formatFileSize(file.sizeBytes)}
                </Typography>
              </Box>
              <Button
                aria-label={`Download ${file.fileName}`}
                onClick={() => void handleDownload(file)}
                disabled={downloadingId === file.id}
                startIcon={
                  downloadingId === file.id ? (
                    <CircularProgress size={14} />
                  ) : (
                    <FileDownloadOutlinedIcon sx={{ width: 16, height: 16 }} />
                  )
                }
                sx={outlinedButtonSx}
              >
                Download
              </Button>
            </Box>
          ))}
        </Box>
      )}
    </ModalShell>
  );
};

export default SurveyRequestFilesDialog;
